import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  Button, 
  Input, 
  InputGroup, 
  InputRightElement,
  useToast,
  Card,
  CardBody,
  Stack,
  Divider,
  CardFooter,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';

const AuthTokenPage = () => {
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  useEffect(() => {
    // Get the Firebase token from localStorage
    const firebaseToken = localStorage.getItem('firebaseToken');
    if (firebaseToken) {
      setToken(firebaseToken);
    }
  }, []);

  const handleCopyToken = () => {
    navigator.clipboard.writeText(token)
      .then(() => {
        setCopied(true);
        toast({
          title: 'Token copied!',
          description: 'The authentication token has been copied to your clipboard.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Reset copied status after 2 seconds
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        toast({
          title: 'Copy failed',
          description: 'Failed to copy token: ' + err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };

  const toggleShowToken = () => {
    setShowToken(!showToken);
  };

  return (
    <Container maxW="container.md" py={8}>
      <Heading as="h1" mb={6}>Authentication Token</Heading>
      
      <Alert status="info" mb={6}>
        <AlertIcon />
        <Box>
          <AlertTitle>For Development & API Access</AlertTitle>
          <AlertDescription>
            This page displays your current authentication token. This token can be used to make authenticated API requests outside the browser, such as in scripts or API tools.
          </AlertDescription>
        </Box>
      </Alert>

      <Card>
        <CardBody>
          <Stack spacing={4}>
            <Text fontWeight="bold">Your current authentication token:</Text>
            <InputGroup size="md">
              <Input
                pr="4.5rem"
                type={showToken ? 'text' : 'password'}
                value={token || 'No token found'}
                isReadOnly
                mb={3}
              />
              <InputRightElement width="4.5rem">
                <Button h="1.75rem" size="sm" onClick={toggleShowToken}>
                  {showToken ? 'Hide' : 'Show'}
                </Button>
              </InputRightElement>
            </InputGroup>
            
            <Text fontSize="sm" color="gray.500">
              This token expires periodically and will need to be refreshed by logging in again.
            </Text>
          </Stack>
        </CardBody>
        <Divider />
        <CardFooter>
          <Button 
            colorScheme="blue"
            onClick={handleCopyToken}
            isDisabled={!token}
            width="full"
          >
            {copied ? 'Copied!' : 'Copy Token'}
          </Button>
        </CardFooter>
      </Card>
    </Container>
  );
};

export default AuthTokenPage;
